/**
 * Module dependencies.
 */

import { Button, icons } from 'src/components/core';
import { ReactElement, useEffect, useState } from 'react';
import { units } from '@untile/react-components';
import FiltersSidebar from './sidebars/filters-sidebar';
import SortSidebar from './sidebars/sort-sidebar';
import styled from 'styled-components';
import useBodyScroll from 'src/hooks/use-body-scroll';
import useModelRangeFilters from 'src/api/model-ranges/use-model-range-filters';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  isUpdating: boolean;
  isUpdatingSucceed: boolean;
  modelRangeFilters: any;
  onUpdate: () => void;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: grid;
  grid-column-gap: ${units(1)};
  grid-template-columns: repeat(2, max-content);
  position: relative;
`;

/**
 * `Filters` component.
 */

const Filters = (props: Props): ReactElement => {
  const { className, isUpdating, modelRangeFilters, onUpdate } = props;
  const { translate } = useTranslate();
  const [isFiltersSidebarOpen, setFiltersSidebarOpen] = useState<boolean>();
  const [isSortSidebarOpen, setSortSidebarOpen] = useState<boolean>();
  const { data, isLoading, isSuccess } = useModelRangeFilters({
    initialData: modelRangeFilters
  });

  useBodyScroll({ off: isFiltersSidebarOpen || isSortSidebarOpen });

  useEffect(() => {
    if (isFiltersSidebarOpen || isSortSidebarOpen) {
      const handleEscKey = (event: KeyboardEvent) => {
        if (event.which === 27) {
          setFiltersSidebarOpen(false);
          setSortSidebarOpen(false);
        }
      };

      document.addEventListener('keydown', handleEscKey, false);

      return () => {
        document.removeEventListener('keydown', handleEscKey, false);
      };
    }
  }, [
    isFiltersSidebarOpen,
    isSortSidebarOpen,
    setFiltersSidebarOpen,
    setSortSidebarOpen
  ]);

  return (
    <>
      <Wrapper className={className}>
        <Button
          colorTheme={'secondary'}
          icon={icons.chevronDown}
          isReversed
          onClick={() => setSortSidebarOpen(true)}
          size={'medium'}
        >
          {translate('common:actions.sortBy')}
        </Button>

        <Button
          colorTheme={'secondary'}
          icon={icons.filter}
          isLoading={isLoading}
          isReversed
          onClick={() => setFiltersSidebarOpen(true)}
          size={'medium'}
        >
          {translate('common:labels.filters')}
        </Button>
      </Wrapper>

      {isSuccess && (
        <>
          <SortSidebar
            availableFilters={data}
            isOpen={isSortSidebarOpen}
            isUpdating={isUpdating}
            onClose={() => setSortSidebarOpen(false)}
            onUpdate={onUpdate}
          />

          <FiltersSidebar
            availableFilters={data}
            isOpen={isFiltersSidebarOpen}
            isUpdating={isUpdating}
            onClose={() => setFiltersSidebarOpen(false)}
            onUpdate={onUpdate}
          />
        </>
      )}
    </>
  );
};

/**
 * Export `Filters` component.
 */

export default Filters;
